import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Profile } from '../types';
import { BackButton } from '../components/BackButton';
import { Breadcrumbs } from '../components/Breadcrumbs';

interface EditProfilePageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const EditProfilePage: React.FC<EditProfilePageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    filiere: '',
    annee: '',
    langue: 'francais' as 'francais' | 'anglais',
    role: 'etudiant' as 'etudiant' | 'repetiteur' | 'les_deux',
    tarif: 30,
    email_contact: '',
    telephone_contact: ''
  });

  const filieres = [
    { value: 'IG', label: 'Informatique de Gestion (IG)' },
    { value: 'EE', label: 'Économie d\'Entreprise (EE)' },
    { value: 'IBM', label: 'International Business Management (IBM)' },
    { value: 'IS', label: 'Information Science (IS)' },
    { value: 'Autre', label: 'Autre' }
  ];


  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await api.profiles.getByUserId(user!.id);
      
      if (error) {
        showToast('Erreur lors du chargement du profil', 'error');
        navigate('/profile');
        return;
      }

      if (data) {
        setProfile(data);

        setFormData({
          prenom: data.prenom || '',
          nom: data.nom || '',
          filiere: data.filiere || '',
          annee: data.annee || '',
          langue: data.langue || 'francais',
          role: data.role || 'etudiant',
          tarif: data.tarif || 30,
          email_contact: (data as any).email_contact || '',
          telephone_contact: (data as any).telephone_contact || ''
        });
      } else {
        // Aucun profil trouvé pour cet utilisateur
        showToast('Vous devez créer un profil avant de pouvoir le modifier.', 'error');
        navigate('/signup');
        return;
      }
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
      navigate('/profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: name === 'tarif' ? Number(value) : value
      };
      
      // Si on change le rôle vers étudiant, mettre le tarif à 0
      if (name === 'role' && value === 'etudiant') {
        newData.tarif = 0;
      }
      
      return newData;
    });
  };


  const validateForm = () => {
    const required = ['prenom', 'nom', 'filiere', 'annee', 'email_contact'];
    
    for (const field of required) {
      if (!formData[field as keyof typeof formData]) {
        showToast(`Le champ ${field === 'email_contact' ? 'email de contact' : field} est obligatoire`, 'error');
        return false;
      }
    }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email_contact)) {
      showToast('Veuillez saisir un email de contact valide', 'error');
      return false;
    }

    // Validation du tarif seulement pour les tuteurs
    if (formData.role !== 'etudiant' && (formData.tarif < 25 || formData.tarif > 60)) {
      showToast('Le tarif horaire doit être entre 25 et 60 CHF', 'error');
      return false;
    }



    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const hp = (document.getElementById('hp_field') as HTMLInputElement | null)?.value;
    if (hp) {
      return;
    }
    
    if (!validateForm() || !profile) {
      return;
    }

    setSaving(true);

    try {
      const { error } = await api.profiles.update(profile.id, {
        prenom: formData.prenom,
        nom: formData.nom,
        filiere: formData.filiere,
        annee: formData.annee,
        langue: formData.langue,
        role: formData.role,
        tarif: formData.role === 'etudiant' ? 0 : formData.tarif,
        email_contact: formData.email_contact,
        telephone_contact: formData.telephone_contact
      });

      if (error) {
        
        // Messages d'erreur plus spécifiques
        let errorMessage = 'Erreur lors de la mise à jour du profil';
        const errorMsg = (error as any).message || '';
        const errorCode = (error as any).code || '';
        
        if (errorMsg.includes('violates check constraint') || errorCode === '23514') {
          errorMessage = 'Données invalides. Vérifiez vos informations (tarif entre 10-100 CHF).';
        } else if (errorMsg.includes('null value') || errorCode === '23502') {
          errorMessage = 'Tous les champs obligatoires doivent être remplis';
        } else if (errorMsg.includes('permission denied') || errorCode === '42501') {
          errorMessage = 'Erreur de permissions. Veuillez vous reconnecter.';
        } else if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
          errorMessage = 'Erreur de connexion. Vérifiez votre connexion internet.';
        }
        
        showToast(errorMessage, 'error');
        return;
      }

      showToast('Profil mis à jour avec succès !', 'success');
      
      // Scroll automatiquement en haut de la page
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Rester dans la page d'édition au lieu de rediriger
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Navigation */}
        <div className="mb-6">
          <BackButton to="/profile" label="Retour au profil" />
          <Breadcrumbs />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-[#002c46] rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-edit text-white text-2xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Modifier mon profil
          </h1>
          <p className="text-gray-600">
            Mettez à jour vos informations personnelles
          </p>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Champ honeypot anti-spam (caché) */}
            <input id="hp_field" name="company" type="text" className="hidden" autoComplete="off" tabIndex={-1} aria-hidden="true" />
            {/* Informations personnelles */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                <i className="fas fa-user text-[#002c46] mr-2"></i>
                Informations personnelles
              </h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    name="prenom"
                    type="text"
                    required
                    value={formData.prenom}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Jean"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    name="nom"
                    type="text"
                    required
                    value={formData.nom}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Dupont"
                  />
                </div>
              </div>


              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Rôle *
                  </label>
                  <select
                    name="role"
                    required
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="etudiant">👨‍🎓 Étudiant-e</option>
                    <option value="repetiteur">👩‍🏫 Tuteur-trice</option>
                    <option value="les_deux">🎓 Les deux</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Langue d'enseignement *
                  </label>
                  <select
                    name="langue"
                    required
                    value={formData.langue}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="francais">🇫🇷 Français</option>
                    <option value="anglais">🇬🇧 Anglais</option>
                  </select>
                </div>
              </div>

              {/* Tarif horaire conditionnel */}
              {formData.role !== 'etudiant' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tarif horaire (CHF/h) *
                  </label>
                  <input
                    name="tarif"
                    type="number"
                    min="25"
                    max="60"
                    required
                    value={formData.tarif}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 100 CHF/h</p>
                </div>
              )}

              {/* Message informatif pour étudiants */}
              {formData.role === 'etudiant' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="fas fa-info-circle text-blue-600 mr-3"></i>
                    <p className="text-blue-800 text-sm">
                      En tant qu'étudiant-e, vous n'avez pas besoin de définir de tarif. 
                      Vous pouvez rechercher et contacter des tuteur-trices.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Informations de contact */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                <i className="fas fa-envelope text-[#002c46] mr-2"></i>
                Informations de contact
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Ces informations ne seront révélées qu'aux étudiants avec lesquels vous aurez un match mutuel.
              </p>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email de contact *
                  </label>
                  <input
                    name="email_contact"
                    type="email"
                    required
                    value={formData.email_contact}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="votre.email@exemple.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Peut être différent de votre email de connexion
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone (optionnel)
                  </label>
                  <input
                    name="telephone_contact"
                    type="tel"
                    value={formData.telephone_contact}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+41 XX XXX XX XX"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Sera révélé uniquement après match mutuel
                  </p>
                </div>
              </div>
            </div>

            {/* Informations académiques */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                <i className="fas fa-graduation-cap text-[#002c46] mr-2"></i>
                Informations académiques
              </h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Filière d'études *
                  </label>
                  <select
                    name="filiere"
                    required
                    value={formData.filiere}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner une filière</option>
                    {filieres.map(filiere => (
                      <option key={filiere.value} value={filiere.value}>{filiere.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Année d'études *
                  </label>
                  <select
                    name="annee"
                    required
                    value={formData.annee}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner</option>
                    <option value="1">1ère année</option>
                    <option value="2">2ème année</option>
                    <option value="3">3ème année</option>
                    <option value="4">4ème année</option>
                  </select>
                </div>
              </div>

            </div>

            {/* Boutons */}
            <div className="flex gap-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-3 px-6 bg-[#002c46] text-white rounded-lg hover:bg-[#001a2a] transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Sauvegarder
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 